<template>
  <div>
    <AppHeader />
    
    <!-- 英雄区 Hero Section -->
    <section
      class="relative h-screen flex items-center justify-center text-white bg-gradient-stone pt-16 overflow-hidden">
      <div class="absolute inset-0">
        <img 
          :alt="$t('home.hero.title')" 
          class="w-full h-full object-cover opacity-40"
          src="/images/home/home-hero-bg.jpg"
          loading="eager"
        >
      </div>
      <div class="relative z-10 text-center px-4 max-w-4xl">
        <h1 class="text-5xl md:text-7xl font-extrabold leading-tight mb-6 animate-fade-in-up">
          {{ $t('home.hero.title') }}
        </h1>
        <p class="text-xl md:text-2xl mb-10 max-w-3xl mx-auto animate-fade-in-up delay-200">
          {{ $t('home.hero.subtitle') }}
        </p>
        <router-link 
          to="/contact"
          class="inline-block bg-white text-purple-700 hover:bg-purple-100 px-8 py-4 rounded-full text-lg font-semibold shadow-lg transition duration-300 transform hover:scale-105 animate-fade-in-up delay-400">
          {{ $t('home.hero.cta') }}
          <i class="fas fa-arrow-right ml-2"></i>
        </router-link>
      </div>
    </section>

    <!-- 新闻滚动条 News Ticker -->
    <section class="bg-gray-50 py-4 px-6 md:px-12 shadow-inner">
      <div class="flex items-center space-x-4 overflow-hidden">
        <i class="fas fa-bullhorn text-xl text-purple-600 flex-shrink-0"></i>
        <div class="flex-grow overflow-hidden">
          <div class="whitespace-nowrap animate-scroll-left" id="news-ticker">
            <span class="inline-block mr-8 text-gray-700">
              <span class="font-semibold text-purple-700">
                {{ $t('home.news.latest') }}：
              </span>
              {{ $t('home.news.item1.content') }}
              <span class="text-gray-500 text-sm ml-2">{{ $t('home.news.item1.date') }}</span>
            </span>
            <span class="inline-block mr-8 text-gray-700">
              <span class="font-semibold text-purple-700">
                {{ $t('home.news.industry') }}：
              </span>
              {{ $t('home.news.item2.content') }}
              <span class="text-gray-500 text-sm ml-2">{{ $t('home.news.item2.date') }}</span>
            </span>
            <span class="inline-block mr-8 text-gray-700">
              <span class="font-semibold text-purple-700">
                {{ $t('home.news.company') }}：
              </span>
              {{ $t('home.news.item3.content') }}
              <span class="text-gray-500 text-sm ml-2">{{ $t('home.news.item3.date') }}</span>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心数据看板 Core Metrics -->
    <CoreMetricsSection />

    <!-- 服务覆盖地图 Service Regions Map -->
    <ServiceRegionsMap />

    <!-- 特色趋势与洞察 Insights -->
    <section class="py-20 px-6 md:px-12 bg-white">
      <h2 class="text-4xl font-extrabold text-center mb-14 text-gray-900">
        {{ $t('home.insights.title') }}
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto">
        <div class="bg-white rounded-xl shadow-xl overflow-hidden transform hover:scale-102 transition duration-300">
          <img 
            :alt="$t('home.insights.digitalTransformation.title')" 
            class="w-full h-52 object-cover"
            src="/images/home/home-digital-transformation.jpg"
            loading="lazy"
          >
          <div class="p-7">
            <h3 class="text-2xl font-bold mb-3 text-gray-900">
              {{ $t('home.insights.digitalTransformation.title') }}
            </h3>
            <p class="text-gray-700 mb-5 leading-relaxed">
              {{ $t('home.insights.digitalTransformation.description') }}
            </p>
          </div>
        </div>
        <div class="bg-white rounded-xl shadow-xl overflow-hidden transform hover:scale-102 transition duration-300">
          <img 
            :alt="$t('home.insights.aiFuture.title')" 
            class="w-full h-52 object-cover"
            src="/images/home/home-ai-future.jpg"
            loading="lazy"
          >
          <div class="p-7">
            <h3 class="text-2xl font-bold mb-3 text-gray-900">
              {{ $t('home.insights.aiFuture.title') }}
            </h3>
            <p class="text-gray-700 mb-5 leading-relaxed">
              {{ $t('home.insights.aiFuture.description') }}
            </p>
          </div>
        </div>
        <div class="bg-white rounded-xl shadow-xl overflow-hidden transform hover:scale-102 transition duration-300">
          <img 
            :alt="$t('home.insights.bigData.title')" 
            class="w-full h-52 object-cover"
            src="/images/home/home-big-data.jpg"
            loading="lazy"
          >
          <div class="p-7">
            <h3 class="text-2xl font-bold mb-3 text-gray-900">
              {{ $t('home.insights.bigData.title') }}
            </h3>
            <p class="text-gray-700 mb-5 leading-relaxed">
              {{ $t('home.insights.bigData.description') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心服务（位于数字化能力区块之上） -->
    <section class="py-20 px-6 md:px-12 bg-slate-50 border-t border-gray-100">
      <div class="max-w-7xl mx-auto">
        <div class="max-w-3xl mx-auto text-center mb-14">
          <p class="text-sm font-semibold tracking-[0.2em] text-slate-500 uppercase mb-3">
            {{ $t('home.coreServices.kicker') }}
          </p>
          <h2 class="text-4xl font-extrabold text-gray-900 mb-4">
            {{ $t('home.coreServices.title') }}
          </h2>
          <p class="text-lg text-slate-600 leading-relaxed">
            {{ $t('home.coreServices.subtitle') }}
          </p>
        </div>

        <div class="core-service-grid">
          <article
            v-for="item in homeCoreServices"
            :key="item.sectionId"
            class="core-service-card group flex h-full flex-col rounded-3xl border border-gray-200 bg-white p-6 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl"
          >
            <div
              :class="[
                'mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-md bg-gradient-to-br',
                item.gradient
              ]"
            >
              <i :class="[item.icon, 'text-xl']"></i>
            </div>
            <p :class="['text-xs font-semibold tracking-[0.08em] mb-3', item.taglineClass]">
              {{ item.eyebrow }}
            </p>
            <h3 class="text-xl font-bold text-gray-900 mb-3 leading-snug">
              {{ item.title }}
            </h3>
            <p class="text-gray-600 leading-7 text-sm grow">
              {{ item.description }}
            </p>
            <router-link
              :to="{ path: '/service', hash: `#${item.sectionId}` }"
              class="mt-5 inline-flex items-center text-sm font-semibold text-purple-700 hover:text-purple-900"
            >
              {{ $t('home.coreServices.viewDetail') }}
              <i class="fas fa-arrow-right ml-2 text-xs transition-transform group-hover:translate-x-0.5"></i>
            </router-link>
          </article>
        </div>

        <div class="mt-12 flex justify-center">
          <router-link
            to="/service"
            class="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-7 py-3 text-sm font-semibold text-gray-800 shadow-sm transition hover:border-purple-300 hover:text-purple-700"
          >
            {{ $t('home.coreServices.viewAllServices') }}
            <i class="fas fa-arrow-right ml-2 text-xs"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- 数字化能力（产品延伸） -->
    <section class="py-20 px-6 md:px-12 bg-gray-50">
      <div class="max-w-7xl mx-auto">
        <div class="max-w-4xl mx-auto text-center mb-14">
          <h2 class="text-4xl font-extrabold text-gray-900 mb-4">
            {{ $t('home.productExtensions.title') }}
          </h2>
          <p class="text-lg text-gray-600 leading-8">
            {{ $t('home.productExtensions.subtitle') }}
          </p>
        </div>

        <div class="product-extension-grid">
          <article
            v-for="item in homeProductExtensions"
            :key="item.id"
            class="product-extension-card h-full rounded-3xl border border-gray-200 bg-white p-6 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl"
          >
            <div :class="['w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-md bg-gradient-to-br mb-5', item.gradient]">
              <i :class="[item.icon, 'text-xl']"></i>
            </div>
            <p :class="['text-xs font-semibold tracking-[0.08em] mb-3', item.taglineClass]">
              {{ item.tagline }}
            </p>
            <h3 class="text-xl font-bold text-gray-900 mb-3 leading-snug">
              {{ item.title }}
            </h3>
            <p class="text-gray-600 leading-7 text-sm">
              {{ item.description }}
            </p>
          </article>
        </div>

        <div class="mt-12 flex items-center justify-center">
          <router-link
            :to="{ path: '/product', query: { section: 'product-capabilities' }, hash: '#product-capabilities' }"
            class="inline-flex items-center justify-center bg-purple-600 text-white hover:bg-purple-700 px-7 py-3 rounded-full font-semibold shadow-lg transition duration-300"
          >
            {{ $t('home.productExtensions.viewAll') }}
            <i class="fas fa-arrow-right ml-3"></i>
          </router-link>
        </div>
      </div>
    </section>

    <!-- 成功案例 Case Studies -->
    <CaseStudiesSection />

    <!-- 立即体验产品 CTA -->
    <section class="py-20 px-6 md:px-12 bg-gradient-stone text-white">
      <div class="max-w-5xl mx-auto text-center">
        <h2 class="text-4xl font-extrabold mb-8">
          {{ $t('home.cta.title') }}
        </h2>
        <p class="text-xl leading-relaxed mb-12 opacity-90">
          {{ $t('home.cta.subtitle') }}
        </p>
        <router-link 
          to="/contact"
          class="inline-block bg-white text-purple-700 hover:bg-purple-100 px-8 py-4 rounded-full text-lg font-semibold shadow-lg transition duration-300 transform hover:scale-105">
          {{ $t('home.cta.contact') }}
          <i class="fas fa-envelope ml-2"></i>
        </router-link>
      </div>
    </section>

    <!-- 底部页脚 Footer -->
    <AppFooter />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSeoMeta } from '@/composables/useSeoMeta'
import { usePerformanceMonitor } from '@/composables/usePerformanceMonitor'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import CoreMetricsSection from '@/components/CoreMetricsSection.vue'
import ServiceRegionsMap from '@/components/ServiceRegionsMap.vue'
import CaseStudiesSection from '@/components/CaseStudiesSection.vue'

const { tm } = useI18n()

const homeCoreServiceDecorations = [
  {
    icon: 'fas fa-desktop',
    gradient: 'from-indigo-500 to-blue-600',
    taglineClass: 'text-indigo-600'
  },
  {
    icon: 'fas fa-network-wired',
    gradient: 'from-slate-600 to-slate-800',
    taglineClass: 'text-slate-600'
  },
  {
    icon: 'fas fa-cloud',
    gradient: 'from-sky-500 to-cyan-600',
    taglineClass: 'text-sky-600'
  },
  {
    icon: 'fas fa-store',
    gradient: 'from-rose-500 to-orange-500',
    taglineClass: 'text-rose-600'
  }
]

const homeCoreServices = computed(() =>
  tm('home.coreServices.items').map((item, index) => ({
    ...item,
    ...homeCoreServiceDecorations[index]
  }))
)

const homeProductExtensionDecorations = [
  {
    id: 'crm-suite',
    icon: 'fas fa-address-book',
    gradient: 'from-sky-500 to-blue-600',
    taglineClass: 'text-sky-700'
  },
  {
    id: 'marketing-automation',
    icon: 'fas fa-bullseye',
    gradient: 'from-emerald-500 to-teal-600',
    taglineClass: 'text-emerald-700'
  },
  {
    id: 'mini-program',
    icon: 'fas fa-mobile-screen-button',
    gradient: 'from-orange-500 to-amber-500',
    taglineClass: 'text-orange-700'
  },
  {
    id: 'ai-knowledge-base',
    icon: 'fas fa-book-open-reader',
    gradient: 'from-violet-500 to-fuchsia-600',
    taglineClass: 'text-violet-700'
  },
  {
    id: 'office-agent',
    icon: 'fas fa-robot',
    gradient: 'from-slate-600 to-slate-800',
    taglineClass: 'text-slate-700'
  }
]

const homeProductExtensions = computed(() => tm('home.productExtensions.items').map((item, index) => ({
  ...item,
  ...homeProductExtensionDecorations[index]
})))

// SEO Meta 标签
useSeoMeta({
  title: {
    zh: '首页 - Global Data',
    en: 'Home - Global Data',
    ja: 'ホーム - 思棟株式会社',
    ko: '홈 - Global Data'
  },
  description: {
    zh: 'Global Data 致力于通过领先的技术解决方案，赋能企业数字化转型，构建智能互联的未来。专注亚太地区IT基础设施服务。',
    en: 'Global Data is committed to empowering enterprise digital transformation through leading technology solutions, building an intelligent and interconnected future. Specialized in Asia-Pacific IT infrastructure services.',
    ja: '思棟株式会社は、先進的な技術ソリューションを通じて企業のデジタル変革を支援し、インテリジェントで相互接続された未来を構築することに取り組んでいます。アジア太平洋地域のITインフラサービスに特化。',
    ko: 'Global Data는 선도적인 기술 솔루션을 통해 기업의 디지털 전환을 지원하고, 더 지능적이고 연결된 미래를 구축하는 데 전념하고 있습니다. 아시아태평양 지역 IT 인프라 서비스에 특화되어 있습니다.'
  }
})

// 性能监控
usePerformanceMonitor({
  enableLongTaskMonitoring: true,
  enableResourceTiming: true,
  logToConsole: process.env.NODE_ENV === 'development'
})
</script>

<style scoped>
@import '@/assets/css/main.css';
@import '@/assets/css/home.css';

/* 核心服务：与数字化能力区块一致的断点策略 — 手机单列，平板双列，桌面四列横排 */
.core-service-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.5rem;
}

.core-service-card {
  min-width: 0;
}

@media (min-width: 768px) {
  .core-service-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .core-service-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.product-extension-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.5rem;
}

.product-extension-card {
  display: flex;
  min-height: 240px;
  flex-direction: column;
}

@media (min-width: 768px) {
  .product-extension-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .product-extension-card:last-child:nth-child(odd) {
    grid-column: 1 / -1;
    justify-self: center;
    width: min(100%, 420px);
  }
}

@media (min-width: 1024px) {
  .product-extension-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .product-extension-card {
    grid-column: span 2;
    min-height: 260px;
  }

  .product-extension-card:nth-child(4) {
    grid-column: 2 / span 2;
  }

  .product-extension-card:last-child:nth-child(odd) {
    grid-column: span 2;
    width: 100%;
  }
}

@media (min-width: 1280px) {
  .product-extension-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .product-extension-card,
  .product-extension-card:nth-child(4),
  .product-extension-card:last-child:nth-child(odd) {
    grid-column: auto;
    min-height: 280px;
  }
}
</style>
